/**
 * The API's error contract.
 *
 * Every non-2xx response the API produces has one shape:
 *
 *   { "error": "<code>", "message": "<English, for logs and developers — never shown to a person>", "details"?: { … } }
 *
 * `error` is the identifier a client branches on. The HTTP status is a function of the code (this table)
 * and is never chosen at the call site. `details` carries only the keys documented per code in
 * docs/api-errors.md. The panel imports this file; the iOS app mirrors it in Core/APIErrors.swift, and a
 * WasityTests static test fails when the two drift.
 */
export const ERROR_CODES = {
  // session
  unauthenticated: 401,
  forbidden: 403,
  account_banned: 403,
  invalid_credentials: 401,
  password_incorrect: 400,
  // one-time code
  invalid_phone: 422,
  invalid_code: 401,
  otp_delivery_failed: 503,
  // rate limit
  too_many_requests: 429,
  // validation
  validation_failed: 422,
  invalid_slot: 422,
  invalid_date: 422,
  // request lifecycle (broker requests and valuations)
  broker_request_not_found: 404,
  evaluation_not_found: 404,
  request_not_found: 404,
  broker_not_found: 404,
  evaluator_not_found: 404,
  invalid_state: 409,
  already_assigned: 409,
  acceptance_window_expired: 409,
  cancel_not_allowed_after_contract: 409,
  cancel_requires_support_after_payment: 409,
  payment_in_progress: 409,
  // membership and slots
  membership_inactive: 409,
  no_free_slots: 409,
  tier_not_found: 404,
  membership_not_found: 404,
  // offers
  offer_not_found: 404,
  offer_already_pending: 409,
  // listings
  listing_not_found: 404,
  listing_exists: 409,
  listing_requires_licence: 409,
  listing_needs_photos: 422,
  listing_needs_viewing_hours: 422,
  // viewings
  viewing_not_found: 404,
  property_not_found: 404,
  cannot_view_own_property: 409,
  slot_unavailable: 409,
  slot_taken: 409,
  not_your_turn: 409,
  viewing_not_started: 409,
  availability_overlap: 409,
  // payments
  no_pending_payment: 409,
  payment_not_paid: 409,
  payment_currency_mismatch: 409,
  payment_amount_mismatch: 409,
  payment_wrong_request: 409,
  payment_already_used: 409,
  paid_after_cancel: 409,
  payment_provider_unavailable: 503,
  // media
  media_not_found: 404,
  purpose_not_allowed: 403,
  unsupported_file_type: 422,
  file_too_large: 422,
  // sign-up, admin, users
  signup_request_not_found: 404,
  signup_already_pending: 409,
  email_in_use: 409,
  phone_in_use: 409,
  user_not_found: 404,
  slug_in_use: 409,
  invalid_signature: 401,
  not_available: 404,
  // generic
  not_found: 404,
  internal_error: 500,
  service_unavailable: 503,
} as const satisfies Record<string, 400 | 401 | 403 | 404 | 409 | 422 | 429 | 500 | 503>;

export type ErrorCode = keyof typeof ERROR_CODES;
export type ErrorStatus = (typeof ERROR_CODES)[ErrorCode];

export const isErrorCode = (v: unknown): v is ErrorCode => typeof v === 'string' && Object.hasOwn(ERROR_CODES, v);

/** The `message` each code carries. English, one sentence, for the log line and the developer reading it raw. */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  unauthenticated: 'No valid session was presented.',
  forbidden: 'This account may not perform this action.',
  account_banned: 'This account has been suspended.',
  invalid_credentials: 'The email or password is wrong.',
  password_incorrect: 'The current password is wrong.',
  invalid_phone: 'The phone number is not a Saudi mobile number.',
  invalid_code: 'The one-time code is wrong or has expired.',
  otp_delivery_failed: 'The one-time code could not be sent.',
  too_many_requests: 'Too many attempts; wait and try again.',
  validation_failed: 'One or more fields are invalid; see details.issues.',
  invalid_slot: 'The viewing time is in the past or is not a valid time.',
  invalid_date: 'The date could not be read.',
  broker_request_not_found: 'No such broker request.',
  evaluation_not_found: 'No such valuation request.',
  request_not_found: 'No request with that number.',
  broker_not_found: 'No such broker.',
  evaluator_not_found: 'No such valuer.',
  invalid_state: 'The request is no longer in a state that allows this; reload it.',
  already_assigned: 'This staff member already holds the request.',
  acceptance_window_expired: 'The window to accept this request has passed.',
  cancel_not_allowed_after_contract: 'A request cannot be cancelled once a contract is signed.',
  cancel_requires_support_after_payment: 'A paid request can only be cancelled through support.',
  payment_in_progress: 'A payment for this request is still being confirmed; try again shortly.',
  membership_inactive: 'The staff member has no active membership.',
  no_free_slots: 'The staff member has no free slot for another request.',
  tier_not_found: 'No such membership tier.',
  membership_not_found: 'No membership on record.',
  offer_not_found: 'No such offer.',
  offer_already_pending: 'An offer is already waiting for the owner.',
  listing_not_found: 'No such listing.',
  listing_exists: 'This request already has a listing.',
  listing_requires_licence: 'A listing needs a licensed request.',
  listing_needs_photos: 'A listing cannot go live without photos.',
  listing_needs_viewing_hours: 'A listing nobody can book a viewing on cannot go live: the property has no viewing hours.',
  viewing_not_found: 'No such viewing.',
  property_not_found: 'No such property.',
  cannot_view_own_property: 'An owner cannot book a viewing of their own property.',
  slot_unavailable: "The requested time is outside the owner's availability.",
  slot_taken: 'Another viewing has already been confirmed for that time.',
  not_your_turn: 'The viewing is waiting on the other party.',
  viewing_not_started: 'The viewing cannot be closed before it happens.',
  availability_overlap: 'That availability window is already set.',
  no_pending_payment: 'There is no payment waiting to be confirmed.',
  payment_not_paid: 'The provider does not report this payment as paid.',
  payment_currency_mismatch: 'The payment is not in SAR.',
  payment_amount_mismatch: 'The payment amount does not match what is due.',
  payment_wrong_request: 'The payment was made for a different request.',
  payment_already_used: 'This payment has already been applied.',
  paid_after_cancel: 'The payment arrived after the request was cancelled; it is recorded and will be refunded.',
  payment_provider_unavailable: 'The payment provider did not answer; try again shortly.',
  media_not_found: 'No such file.',
  purpose_not_allowed: 'This account may not upload files for that purpose.',
  unsupported_file_type: 'That file type is not accepted here.',
  file_too_large: 'The file is larger than allowed.',
  signup_request_not_found: 'No such sign-up request.',
  signup_already_pending: 'A sign-up request with these details is already waiting for review.',
  email_in_use: 'That email already belongs to an account.',
  phone_in_use: 'That phone number already belongs to an account.',
  user_not_found: 'No such user.',
  slug_in_use: 'That slug is already taken.',
  invalid_signature: 'The webhook signature did not verify.',
  not_available: 'Not available in this environment.',
  not_found: 'No such route.',
  internal_error: 'Something went wrong on our side.',
  service_unavailable: 'A service we depend on did not answer; try again shortly.',
};

/**
 * Codes a client must word specifically. Everything else may fall back to a generic sentence; these
 * describe a situation the person can act on, so the panel and the app each carry a sentence per code
 * (their tests assert it).
 */
export const RENDERED_ERROR_CODES = [
  'unauthenticated',
  'forbidden',
  'account_banned',
  'invalid_credentials',
  'password_incorrect',
  'invalid_phone',
  'invalid_code',
  'otp_delivery_failed',
  'too_many_requests',
  'validation_failed',
  'invalid_slot',
  'broker_request_not_found',
  'evaluation_not_found',
  'request_not_found',
  'broker_not_found',
  'evaluator_not_found',
  'invalid_state',
  'already_assigned',
  'acceptance_window_expired',
  'cancel_not_allowed_after_contract',
  'cancel_requires_support_after_payment',
  'payment_in_progress',
  'membership_inactive',
  'no_free_slots',
  'offer_already_pending',
  'listing_not_found',
  'listing_exists',
  'listing_requires_licence',
  'listing_needs_photos',
  'listing_needs_viewing_hours',
  'cannot_view_own_property',
  'slot_unavailable',
  'slot_taken',
  'not_your_turn',
  'viewing_not_started',
  'availability_overlap',
  'no_pending_payment',
  'payment_not_paid',
  'payment_currency_mismatch',
  'payment_amount_mismatch',
  'payment_wrong_request',
  'payment_already_used',
  'paid_after_cancel',
  'payment_provider_unavailable',
  'unsupported_file_type',
  'file_too_large',
  'signup_already_pending',
  'email_in_use',
  'phone_in_use',
  'service_unavailable',
] as const satisfies readonly ErrorCode[];

/**
 * One broken rule inside `validation_failed.details.issues`. `rule` is the language-neutral part a
 * client words in Arabic or English; `message` is zod's English and is there for whoever reads the API raw.
 */
export type IssueRule = { kind: 'required' | 'minLength' | 'maxLength' | 'minValue' | 'maxValue' | 'invalid'; n?: number };
export interface ValidationIssue {
  /** Dotted path into the request body, `''` when the field is not known, or a header name. */
  path: string;
  rule: IssueRule;
  message?: string;
}

/** What is on the wire. */
export interface ApiErrorBody {
  error: ErrorCode;
  message: string;
  details?: Record<string, unknown>;
}
